import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
    ArrowLeft, Loader2, LogOut, Download, Mail, RefreshCw, ShieldCheck, Search, AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoginScreen = ({ onLogin }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await onLogin(email.trim().toLowerCase(), password);
        } catch (err) {
            setError(err.message || "Errore. Riprova.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main className="min-h-screen bg-black text-white flex items-center justify-center px-5 py-12 relative overflow-hidden">
            <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />
            <div className="absolute top-0 right-0 w-[420px] h-[420px] rounded-full bg-fw/10 blur-[150px] pointer-events-none" />

            <div className="relative z-10 w-full max-w-md">
                <Link
                    to="/"
                    data-testid="admin-back-home"
                    className="inline-flex items-center gap-2 text-white/55 hover:text-fw transition-colors font-mono text-[11px] uppercase tracking-[0.24em] mb-8"
                >
                    <ArrowLeft className="w-4 h-4" strokeWidth={2} />
                    Torna al sito
                </Link>

                <div className="border border-white/12 bg-white/[0.02] backdrop-blur-sm rounded-sm p-8 md:p-10">
                    <div className="flex items-center gap-3 mb-6">
                        <span className="flex-none w-10 h-10 rounded-full bg-fw/15 border border-fw/40 flex items-center justify-center text-fw">
                            <ShieldCheck className="w-5 h-5" strokeWidth={2} />
                        </span>
                        <div>
                            <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-fw">Area Riservata</p>
                            <h1 className="font-heavy text-2xl uppercase leading-tight">Accesso Admin</h1>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} data-testid="admin-login-form" className="space-y-5">
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/55">Email</span>
                            <input
                                type="email"
                                required
                                autoFocus
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                data-testid="admin-input-email"
                                autoComplete="email"
                                className="mt-2 w-full bg-transparent border-b border-white/25 focus:border-fw outline-none py-2 text-white placeholder-white/30 transition-colors"
                                placeholder="fit.wellness2021@gmail.com"
                            />
                        </label>
                        <label className="block">
                            <span className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/55">Password</span>
                            <input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="admin-input-password"
                                autoComplete="current-password"
                                className="mt-2 w-full bg-transparent border-b border-white/25 focus:border-fw outline-none py-2 text-white placeholder-white/30 transition-colors"
                                placeholder="••••••••"
                            />
                        </label>

                        {error && (
                            <p data-testid="admin-login-error" className="flex items-start gap-2 text-sm text-red-400">
                                <AlertTriangle className="w-4 h-4 flex-none mt-0.5" strokeWidth={2} />
                                <span>{error}</span>
                            </p>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="admin-login-submit"
                            className="w-full flex items-center justify-center gap-3 px-7 py-4 bg-fw text-black font-display font-semibold text-sm uppercase tracking-[0.18em] rounded-sm hover:bg-fw-glow hover:glow-green disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                                    Accesso in corso…
                                </>
                            ) : (
                                <>Entra</>
                            )}
                        </button>
                    </form>
                </div>

                <p className="mt-6 text-center text-[11px] text-white/40 leading-relaxed">
                    Accesso limitato. I tentativi falliti vengono tracciati e l&apos;account si blocca temporaneamente dopo 5 errori.
                </p>
            </div>
        </main>
    );
};

const groupLeadsByDay = (leads, days = 30) => {
    const buckets = new Map();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Pre-fill empty days so the chart always shows the full window
    for (let i = days - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(d.getDate() - i);
        buckets.set(d.toISOString().slice(0, 10), 0);
    }
    leads.forEach((l) => {
        if (!l.timestamp) return;
        const d = new Date(l.timestamp);
        if (isNaN(d.getTime())) return;
        const key = d.toISOString().slice(0, 10);
        if (buckets.has(key)) buckets.set(key, buckets.get(key) + 1);
    });
    return Array.from(buckets, ([date, count]) => ({ date, count }));
};

const LeadsChart = ({ leads }) => {
    const data = groupLeadsByDay(leads, 30);
    const max = Math.max(1, ...data.map((d) => d.count));
    const fmtDay = (iso) => {
        const d = new Date(iso);
        return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
    };

    return (
        <div data-testid="admin-leads-chart" className="border border-white/12 bg-white/[0.02] rounded-sm p-5 md:p-6 mb-6">
            <div className="flex items-center justify-between mb-5">
                <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/55">
                    Lead per giorno · ultimi 30 giorni
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-fw">
                    Max {max} / giorno
                </p>
            </div>
            <div className="flex items-end gap-[3px] h-32">
                {data.map((d) => {
                    const h = d.count === 0 ? 2 : Math.max(4, (d.count / max) * 110);
                    return (
                        <div
                            key={d.date}
                            className="group relative flex-1 flex flex-col justify-end items-center"
                        >
                            <div
                                style={{ height: `${h}px` }}
                                className={`w-full rounded-t-sm transition-all duration-200 ${
                                    d.count > 0 ? "bg-fw group-hover:bg-fw-glow" : "bg-white/10"
                                }`}
                                aria-label={`${d.count} lead il ${d.date}`}
                            />
                            {/* Tooltip on hover */}
                            <div className="pointer-events-none absolute -top-9 left-1/2 -translate-x-1/2 px-2 py-1 bg-black border border-fw/40 rounded-sm opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                <p className="font-mono text-[10px] text-fw">{fmtDay(d.date)}</p>
                                <p className="font-display text-xs text-white font-semibold text-center">{d.count}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="flex items-center justify-between mt-3 font-mono text-[10px] text-white/40">
                <span>{fmtDay(data[0]?.date)}</span>
                <span>{fmtDay(data[data.length - 1]?.date)}</span>
            </div>
        </div>
    );
};

const StatCard = ({ label, value }) => (
    <div className="border border-white/12 bg-white/[0.02] rounded-sm p-5">
        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-white/55">{label}</p>
        <p className="font-heavy text-4xl text-white mt-2">{value}</p>
    </div>
);

const matchesQuery = (lead, query) => {
    if (!query) return true;
    const q = query.toLowerCase();
    const fields = [
        lead.first_name, lead.last_name, lead.email,
        lead.nationality, lead.resource_title, lead.resource,
    ];
    return fields.some((f) => (f || "").toLowerCase().includes(q));
};

const fmtDateTime = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString("it-IT", {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch (_e) { return iso; }
};

const LeadsTable = ({ leads, query }) => {
    const filtered = leads.filter((l) => matchesQuery(l, query));

    if (filtered.length === 0) {
        return (
            <div className="border border-white/10 bg-white/[0.02] rounded-sm p-10 text-center">
                <p className="text-white/55 text-sm">Nessun lead trovato.</p>
            </div>
        );
    }

    return (
        <div data-testid="admin-leads-table" className="overflow-x-auto border border-white/10 bg-white/[0.02] rounded-sm">
            <table className="w-full text-sm">
                <thead>
                    <tr className="text-left border-b border-white/10">
                        <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">Data</th>
                        <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">Nome</th>
                        <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">Email</th>
                        <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">Nazion.</th>
                        <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">Lingua</th>
                        <th className="px-4 py-3 font-mono text-[10px] uppercase tracking-[0.22em] text-white/55">Risorsa</th>
                    </tr>
                </thead>
                <tbody>
                    {filtered.map((lead) => (
                        <tr key={lead.id} className="border-b border-white/5 hover:bg-fw/[0.03] transition-colors">
                            <td className="px-4 py-3 text-white/70 whitespace-nowrap">{fmtDateTime(lead.timestamp)}</td>
                            <td className="px-4 py-3 text-white">{lead.first_name} {lead.last_name}</td>
                            <td className="px-4 py-3 text-fw">
                                <a href={`mailto:${lead.email}`} className="hover:underline inline-flex items-center gap-1.5">
                                    <Mail className="w-3 h-3" strokeWidth={2} />
                                    {lead.email}
                                </a>
                            </td>
                            <td className="px-4 py-3 text-white/70">{lead.nationality || "—"}</td>
                            <td className="px-4 py-3 text-white/70 uppercase font-mono text-[11px]">{lead.language}</td>
                            <td className="px-4 py-3 text-white/85">{lead.resource_title || lead.resource || "—"}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className="px-4 py-3 border-t border-white/10 text-[11px] text-white/45 font-mono uppercase tracking-[0.2em]">
                {filtered.length} {filtered.length === 1 ? "risultato" : "risultati"}{query ? ` · filtro: "${query}"` : ""}
            </div>
        </div>
    );
};

const isSameDay = (iso) => {
    if (!iso) return false;
    const d = new Date(iso);
    return d.toDateString() === new Date().toDateString();
};

const isWithinLast7Days = (iso) => {
    if (!iso) return false;
    return new Date(iso) >= new Date(Date.now() - 7 * 86400000);
};

const DashboardHeader = ({ user, loading, onRefresh, onLogout }) => (
    <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
            <div className="flex items-center gap-3 mb-3">
                <span className="w-10 h-px bg-fw" />
                <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-fw">Admin Dashboard</span>
            </div>
            <h1 className="font-heavy text-4xl md:text-5xl uppercase leading-[0.95]">Lead Area Risorse</h1>
            <p className="mt-2 text-white/55 text-sm font-mono">{user.email}</p>
        </div>
        <div className="flex flex-wrap gap-3">
            <button
                onClick={onRefresh}
                disabled={loading}
                data-testid="admin-refresh-button"
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/20 hover:border-fw text-white hover:text-fw transition-colors font-mono text-[11px] uppercase tracking-[0.18em] rounded-sm"
            >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={2} />
                Aggiorna
            </button>
            <a
                href={`${API}/resources/leads.csv`}
                data-testid="admin-csv-export"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-fw text-black hover:bg-fw-glow font-display font-semibold text-[12px] uppercase tracking-[0.18em] rounded-sm transition-colors"
            >
                <Download className="w-4 h-4" strokeWidth={2} />
                Esporta CSV
            </a>
            <button
                onClick={onLogout}
                data-testid="admin-logout-button"
                className="inline-flex items-center gap-2 px-4 py-2.5 border border-white/15 hover:border-red-400 text-white/70 hover:text-red-400 transition-colors font-mono text-[11px] uppercase tracking-[0.18em] rounded-sm"
            >
                <LogOut className="w-4 h-4" strokeWidth={2} />
                Esci
            </button>
        </div>
    </header>
);

const Dashboard = ({ user, onLogout }) => {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [query, setQuery] = useState("");

    const fetchLeads = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const resp = await fetch(`${API}/resources/leads?limit=1000`, { credentials: "include" });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const data = await resp.json();
            setLeads(data.leads || []);
        } catch (err) {
            setError(err.message || "Errore nel caricamento dei lead.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchLeads(); }, [fetchLeads]);

    const totalToday = leads.filter((l) => isSameDay(l.timestamp)).length;
    const totalWeek = leads.filter((l) => isWithinLast7Days(l.timestamp)).length;

    return (
        <main className="min-h-screen bg-black text-white py-10 md:py-14 px-5 md:px-10 lg:px-16 relative overflow-hidden">
            <div className="absolute inset-0 grid-lines opacity-25 pointer-events-none" />
            <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-fw/10 blur-[150px] pointer-events-none" />

            <div className="relative z-10 mx-auto max-w-[1400px]">
                <DashboardHeader
                    user={user}
                    loading={loading}
                    onRefresh={fetchLeads}
                    onLogout={onLogout}
                />

                <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                    <StatCard label="Lead totali" value={leads.length} />
                    <StatCard label="Ultimi 7 giorni" value={totalWeek} />
                    <StatCard label="Oggi" value={totalToday} />
                </section>

                {!loading && leads.length > 0 && <LeadsChart leads={leads} />}

                <div className="mb-5 flex items-center gap-3 px-4 py-3 border border-white/12 bg-white/[0.02] rounded-sm max-w-md">
                    <Search className="w-4 h-4 text-white/45 flex-none" strokeWidth={2} />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        data-testid="admin-search-input"
                        placeholder="Cerca nome, email, nazionalità, risorsa…"
                        className="flex-1 bg-transparent outline-none text-sm text-white placeholder-white/30"
                    />
                </div>

                {error && (
                    <p data-testid="admin-error" className="mb-4 flex items-center gap-2 text-sm text-red-400">
                        <AlertTriangle className="w-4 h-4" strokeWidth={2} />
                        {error}
                    </p>
                )}

                {loading ? (
                    <div className="flex items-center gap-3 text-white/55 text-sm py-12 justify-center">
                        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                        Caricamento lead…
                    </div>
                ) : (
                    <LeadsTable leads={leads} query={query} />
                )}
            </div>
        </main>
    );
};

export const Admin = () => {
    const { user, login, logout } = useAuth();

    if (user === null) {
        return (
            <main className="min-h-screen bg-black text-white flex items-center justify-center">
                <div className="flex items-center gap-3 text-white/55 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
                    Caricamento…
                </div>
            </main>
        );
    }

    if (!user) {
        return <LoginScreen onLogin={login} />;
    }

    return <Dashboard user={user} onLogout={logout} />;
};

export default Admin;
